import React, { useState, useEffect, useContext, createContext } from 'react';

type ScreenClass = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const BREAKPOINTS: Record<ScreenClass, number> = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600
};

const SCREEN_CLASSES: ScreenClass[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

function getScreenClass(): ScreenClass {
  if (typeof window === 'undefined') return 'xxl';
  const width = window.innerWidth;
  for (let i = SCREEN_CLASSES.length - 1; i >= 0; i--) {
    if (width >= BREAKPOINTS[SCREEN_CLASSES[i]]) {
      return SCREEN_CLASSES[i];
    }
  }
  return 'xs';
}

const ScreenClassContext = createContext<ScreenClass | null>(null);

export const ScreenClassProvider: React.FC<{ children?: React.ReactNode }> = ({
  children
}) => {
  const [screenClass, setScreenClass] = useState<ScreenClass>(getScreenClass);

  useEffect(() => {
    const handleResize = () => {
      setScreenClass(getScreenClass());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ScreenClassContext.Provider value={screenClass}>
      {children}
    </ScreenClassContext.Provider>
  );
};

export type { ScreenClass };
export { SCREEN_CLASSES };

export function useScreenClass(): ScreenClass {
  const ctx = useContext(ScreenClassContext);

  // Fallback: no provider in tree – maintain own listener (backwards-compat)
  const [screenClass, setScreenClass] = useState<ScreenClass>(getScreenClass);

  useEffect(() => {
    if (ctx !== null) {
      // Context is provided; rely on provider and do not attach a separate listener.
      return;
    }
    const handleResize = () => {
      setScreenClass(getScreenClass());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [ctx]);

  return ctx !== null ? ctx : screenClass;
}
